const urlMaxLength = 2000

const dashboardAuthorizerConfig = {
  validTypes: ['speaker', 'sponsor', 'exhibitor', 'homeland'],
  validRegions: ['NORTH_AMERICA_EUROPE', 'ASIA_PACIFIC', 'LATIN_AMERICA', 'AFRICA', 'MENA'],
}

const dashboardStateTypes = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  DECLINED: 'DECLINED',
  REVOKED: 'REVOKED',
}

const formsConfig = {
  largeTextBox: 1000,
  mediumTextBox: 550,
  smallTextBox: 254,
  titleMaxLength: 75,
  problemMaxLength: 160,
  descriptionMaxLength: 200,
}

// How many of each content type a booth may upload. The dashboard shows these to the user as an
// "X of Y" counter next to each section title, so they need to stay in sync with what's enforced.
// High resolution logos are deliberately absent: a booth has two named logo fields in Brightspot,
// so HRLogoCards derives that limit from the slots themselves and no value here could raise it.
const uploadLimits = {
  promotions: 6,
  downloadableResources: {
    admin: 100,
    sponsor: 10,
    default: 2,
  },
}

const callForPresentationsConfig = {
  supportedLanguages: [
    'de',
    'en',
    'es',
    'fr',
    'hu',
    'id',
    'it',
    'ja',
    'km',
    'ko',
    'nl',
    'pl',
    'pt',
    'sm',
    'th',
    'to',
    'vi',
    'zh',
    'zh-hans',
    'zh-Hant',
  ],
}

const proposalTransitionIds = {
  integration: 'initial-74-219982c0-12f9-11ef-ae39-bda99d568b45',
  beta: 'initial-74-219982c0-12f9-11ef-ae39-bda99d568b45',
  www: 'initial-74-219982c0-12f9-11ef-ae39-bda99d568b45',
}

const sponsorSubmitTransitionIds = {
  integration: 'initial-78-f75f3cf0-9180-11ef-8b3d-a180798d3a11',
  beta: 'initial-78-f75f3cf0-9180-11ef-8b3d-a180798d3a11',
  www: 'initial-78-f75f3cf0-9180-11ef-8b3d-a180798d3a11',
}

const exhibitorSubmitTransitionIds = {
  integration: 'initial-0-fbdc6d50-98f5-11ed-a23d-a501f5dfe716',
  beta: 'initial-0-fbdc6d50-98f5-11ed-a23d-a501f5dfe716',
  www: 'initial-0-fbdc6d50-98f5-11ed-a23d-a501f5dfe716',
}

const homelandSubmitTransitionIds = {
  integration: 'initial-77-6366eb30-63e3-11ef-8a7a-2798c58c96f2',
  beta: 'initial-77-6366eb30-63e3-11ef-8a7a-2798c58c96f2',
  www: 'initial-77-6366eb30-63e3-11ef-8a7a-2798c58c96f2',
}

const sessionStates = {
  DRAFT: 'DRAFT',
  WORKFLOW: 'WORKFLOW',
  PUBLISHED: 'PUBLISHED',
  SUBMITTED: 'SUBMITTED',
  DECLINED: 'DECLINED',
  ACCEPTED: 'ACCEPTED',
  EDITABLE: 'EDITABLE',
  TRASHED: 'TRASHED',
}

// Incoming Proposal, Rejected Proposal, Accepted Proposal, Signed Proposal, Submitted Session
const sessionWorkflowStates = {
  '74-a0cb39f0-12f7-11ef-ae39-bda99d568b45': 'SUBMITTED', // qa4, incoming proposal
  '73-8a062bf0-d1ff-11ee-a55f-f11b96d40b80': 'DECLINED', // www, rejected proposal
  '75-b7a942c0-12f7-11ef-ae39-bda99d568b45': 'ACCEPTED', // www, accepted proposal
  '76-c862e080-12f7-11ef-ae39-bda99d568b45': 'EDITABLE', // www, signed proposal
  '77-29c7a310-63e3-11ef-8a7a-2798c58c96f2': 'SUBMITTED', // www & qa4, Skip Proposal (Homelands)
  '69-0279ab00-535f-11ed-89f1-b5840d7c88e8': 'SUBMITTED', // www Assign for Review
  '78-e0c6c8a0-9180-11ef-8b3d-a180798d3a11': 'SUBMITTED', // www & qa4, Initial Review
}

const exhibitors = {
  familysearch: '0000017c-dcfc-d148-a77d-dcffcf810000',
}

const mainStageUpload = {
  url: 'https://rootstech.portal.massive.io/',
}

const videoTypes = {
  live: 'WebinarVideoProvider',
  youtube: 'YoutubeVideoProvider',
  prerecorded: 'HostedVideoProvider',
}

const sessionLocationMap = {
  'Pre-recorded': 'ONLINE',
  'In Person Only': 'IN_PERSON',
  Panel: 'IN_PERSON',
  Workshop: 'IN_PERSON',
  'Live Webinar': 'ONLINE',
}

// Explicit backend StatusValue -> UI lozenge bucket mapping (see StatusValue.java in
// rootstech-call-for-content). Only the 8 shared/terminal values are listed; every other
// StatusValue is one of the ~80 granular per-pipeline-step values (e.g. LIVE_PLACEHOLDER_CREATED)
// and is an active, non-terminal step - those default to IN_PROGRESS, see
// getVideoProcessingStatusBucket.
const videoProcessingStatusBuckets = {
  NOT_STARTED: 'NOT_STARTED',
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  ERROR: 'ERROR',
  FAILED: 'ERROR',
  // Both raw StatusValues land in the same UI "Trash" bucket - the dashboard only ever shows a
  // single Trash affordance, not a distinction between a soft-deleted record and a trashed one.
  DELETED: 'TRASHED',
  TRASHED: 'TRASHED',
}

// Hover preview length for the Error badge tooltip - based on the ~140 char guidance zion-ui's
// own DescriptionTooltip documents for its description prop, applied here to the label instead
// (the truncated error message itself, not the "click for more" hint). The full, untruncated
// message is always available via the click-through popup.
const videoProcessingErrorTooltipMaxLength = 140

// "View by" filter values for the Video Tracking Dashboard, matching the Figma dropdown exactly
// (file smts2b36HyiXXdczoMZBKM, node 1194:3416) - note there is deliberately no "Not Started"
// value there, even though it's one of the 5 lozenge buckets. Labels live in
// VideoProcessingStatusFilter itself (not here) since they need useTranslation.
const videoProcessingStatusFilters = [
  { value: 'ALL' },
  { value: 'ERROR' },
  { value: 'PENDING' },
  { value: 'IN_PROGRESS' },
  { value: 'COMPLETED' },
]

const attachmentWorkflow = {
  draft: 'initial-1-f5931920-8d9f-11f0-9e82-b3d733cd0b67',
  review: '1-0-07aedef0-8da0-11f0-9e82-b3d733cd0b67',
  state: {
    draft: '1-e8971fa0-8d9f-11f0-9e82-b3d733cd0b67',
    review: '0-faa55c00-4478-11ef-9bea-419f3a72f364',
  },
}

export {
  urlMaxLength,
  dashboardAuthorizerConfig,
  dashboardStateTypes,
  formsConfig,
  uploadLimits,
  callForPresentationsConfig,
  proposalTransitionIds,
  sponsorSubmitTransitionIds,
  exhibitorSubmitTransitionIds,
  homelandSubmitTransitionIds,
  sessionStates,
  sessionWorkflowStates,
  sessionLocationMap,
  exhibitors,
  mainStageUpload,
  videoTypes,
  attachmentWorkflow,
  videoProcessingStatusBuckets,
  videoProcessingErrorTooltipMaxLength,
  videoProcessingStatusFilters,
}
